import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatMonthYear, formatMonthName, getShortDayNames } from '../../lib/format-utils';
import { useAuth } from '../../context/AuthContext';
import { 
    getVacationBalance, 
    getMyVacationPlans, 
    createVacationPlan, 
    cancelVacationPlan,
    getRedDays
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
    Calendar, Plus, X, Check, Clock, AlertCircle, Trash2, 
    ChevronLeft, ChevronRight, LayoutList, LayoutGrid, Eye, Pencil
} from 'lucide-react';

export default function VacationPlannerPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState(null);
    const [plans, setPlans] = useState([]);
    const [redDays, setRedDays] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // View/Edit plan dialog
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    
    // View settings
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'gantt'
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [viewRange, setViewRange] = useState('month'); // 'month' or 'quarter'
    
    const [newPlan, setNewPlan] = useState({
        start_date: '',
        end_date: '',
        notes: ''
    });

    useEffect(() => {
        fetchData();
    }, [currentYear]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [balanceRes, plansRes, redDaysRes] = await Promise.all([
                getVacationBalance(),
                getMyVacationPlans(),
                getRedDays(currentYear)
            ]);
            setBalance(balanceRes.data);
            setPlans(plansRes.data);
            setRedDays(redDaysRes.data?.red_days || []);
        } catch (error) {
            toast.error(t('vacation.load_error'));
        } finally {
            setLoading(false);
        }
    };

    const openNewPlanDialog = () => {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        setNewPlan({
            start_date: tomorrow.toISOString().split('T')[0],
            end_date: tomorrow.toISOString().split('T')[0],
            notes: ''
        });
        setDialogOpen(true);
    };

    const handleCreatePlan = async () => {
        if (!newPlan.start_date || !newPlan.end_date) {
            toast.error(t('vacation.create.no_dates_error'));
            return;
        }
        
        setSaving(true);
        try {
            await createVacationPlan(newPlan);
            toast.success(t('vacation.create.success_toast'));
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('vacation.create.error'));
        } finally {
            setSaving(false);
        }
    };

    const handleCancelPlan = async (planId) => {
        if (!confirm(t('vacation.cancel.confirm_dialog'))) return;

        try {
            await cancelVacationPlan(planId);
            toast.success(t('vacation.cancel.success_toast'));
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('vacation.cancel.error'));
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'bg-green-600';
            case 'rejected': return 'bg-destructive';
            case 'cancelled': return 'bg-gray-500';
            default: return 'bg-amber-500';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <Check className="w-3 h-3" />;
            case 'rejected': return <X className="w-3 h-3" />;
            case 'cancelled': return <X className="w-3 h-3" />;
            default: return <Clock className="w-3 h-3" />;
        }
    };

    // Get Swedish week number
    const getWeekNumber = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    };

    // Generate days for the timeline
    const timelineDays = useMemo(() => {
        const days = [];
        let startDate, endDate;
        
        if (viewRange === 'quarter') {
            const quarterStart = Math.floor(currentMonth / 3) * 3;
            startDate = new Date(currentYear, quarterStart, 1);
            endDate = new Date(currentYear, quarterStart + 3, 0);
        } else {
            startDate = new Date(currentYear, currentMonth, 1);
            endDate = new Date(currentYear, currentMonth + 1, 0);
        }
        
        let current = new Date(startDate);
        while (current <= endDate) {
            const dateStr = current.toISOString().split('T')[0];
            days.push({
                date: dateStr,
                day: current.getDate(),
                dayOfWeek: current.getDay(),
                isWeekend: current.getDay() === 0 || current.getDay() === 6,
                isRedDay: redDays.includes(dateStr),
                month: current.getMonth(),
                weekNumber: getWeekNumber(current)
            });
            current.setDate(current.getDate() + 1);
        }
        return days;
    }, [currentYear, currentMonth, viewRange, redDays]);

    // Group days by week for header
    const weekGroups = useMemo(() => {
        const groups = [];
        let currentWeek = null;
        let weekDays = [];
        
        timelineDays.forEach(day => {
            if (currentWeek !== day.weekNumber) {
                if (weekDays.length > 0) {
                    groups.push({ week: currentWeek, days: weekDays });
                }
                currentWeek = day.weekNumber;
                weekDays = [day];
            } else {
                weekDays.push(day);
            }
        });
        if (weekDays.length > 0) {
            groups.push({ week: currentWeek, days: weekDays });
        }
        return groups;
    }, [timelineDays]);

    // Filter plans for current view
    const plansInView = useMemo(() => {
        if (timelineDays.length === 0) return [];
        
        const firstDay = timelineDays[0].date;
        const lastDay = timelineDays[timelineDays.length - 1].date;
        
        return plans.filter(plan => {
            // Check if plan overlaps with current view
            return plan.start_date <= lastDay && plan.end_date >= firstDay;
        });
    }, [plans, timelineDays]);

    // Calculate vacation bar position and width
    const getVacationBarStyle = (vacation) => {
        const startDate = new Date(vacation.start_date);
        const endDate = new Date(vacation.end_date);
        
        const firstDay = timelineDays[0];
        const lastDay = timelineDays[timelineDays.length - 1];
        const firstDate = new Date(firstDay?.date);
        const lastDate = new Date(lastDay?.date);
        
        // Clamp to visible range
        const visibleStart = startDate < firstDate ? firstDate : startDate;
        const visibleEnd = endDate > lastDate ? lastDate : endDate;
        
        // Find indices
        const startIdx = timelineDays.findIndex(d => d.date === visibleStart.toISOString().split('T')[0]);
        const endIdx = timelineDays.findIndex(d => d.date === visibleEnd.toISOString().split('T')[0]);
        
        if (startIdx === -1 || endIdx === -1) return null;
        
        const dayWidth = 100 / timelineDays.length;
        const left = startIdx * dayWidth;
        const width = (endIdx - startIdx + 1) * dayWidth;
        
        return { left: `${left}%`, width: `${width}%` };
    };

    // Calculate planned days
    const pendingDays = plans
        .filter(p => p.status === 'pending' || p.status === 'approved')
        .reduce((sum, p) => sum + p.days_count, 0);

    const shortDayNames = getShortDayNames();

    const navigatePeriod = (direction) => {
        if (viewRange === 'quarter') {
            const newMonth = currentMonth + (direction * 3);
            if (newMonth < 0) {
                setCurrentMonth(9);
                setCurrentYear(currentYear - 1);
            } else if (newMonth > 11) {
                setCurrentMonth(0);
                setCurrentYear(currentYear + 1);
            } else {
                setCurrentMonth(newMonth);
            }
        } else {
            const newMonth = currentMonth + direction;
            if (newMonth < 0) {
                setCurrentMonth(11);
                setCurrentYear(currentYear - 1);
            } else if (newMonth > 11) {
                setCurrentMonth(0);
                setCurrentYear(currentYear + 1);
            } else {
                setCurrentMonth(newMonth);
            }
        }
    };

    const getPeriodLabel = () => {
        if (viewRange === 'quarter') {
            const quarterStart = Math.floor(currentMonth / 3) * 3;
            return `Q${Math.floor(currentMonth / 3) + 1} ${currentYear} (${formatMonthName(currentYear, quarterStart + 1)} - ${formatMonthName(currentYear, quarterStart + 3)})`;
        }
        return formatMonthYear(currentYear, currentMonth + 1);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="vacation-planner-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
                        <Calendar className="w-6 h-6 text-primary" />
                        {t('vacation.page_title')}
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('vacation.page_subtitle')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant={viewMode === 'list' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewMode('list')}
                    >
                        <LayoutList className="w-4 h-4 mr-1" />
                        {t('vacation.view_mode.list')}
                    </Button>
                    <Button
                        variant={viewMode === 'gantt' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewMode('gantt')}
                        className="hidden sm:inline-flex"
                    >
                        <LayoutGrid className="w-4 h-4 mr-1" />
                        {t('vacation.view_mode.calendar')}
                    </Button>
                    <Button onClick={openNewPlanDialog} data-testid="new-vacation-plan-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('vacation.new_plan_button')}
                    </Button>
                </div>
            </div>

            {/* Balance Summary */}
            <div className="grid grid-cols-4 gap-4">
                <Card className="min-w-0">
                    <CardContent className="pt-6">
                        <div className="text-center min-w-0">
                            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground leading-tight">{t('vacation.balance.paid_label')}</p>
                            <p className="text-xl sm:text-3xl font-bold font-mono mt-1 truncate">{balance?.vacation_days || 0}</p>
                            <p className="text-[10px] text-muted-foreground">days</p>
                        </div>
                    </CardContent>
                </Card>

                <Card className="min-w-0">
                    <CardContent className="pt-6">
                        <div className="text-center min-w-0">
                            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground leading-tight">{t('vacation.balance.advance_label')}</p>
                            <p className="text-xl sm:text-3xl font-bold font-mono mt-1 truncate">
                                {(balance?.advance_vacation_days || 0) - (balance?.advance_vacation_used || 0)}
                            </p>
                            <p className="text-[10px] text-muted-foreground">days</p>
                        </div>
                    </CardContent>
                </Card>

                <Card className="min-w-0">
                    <CardContent className="pt-6">
                        <div className="text-center min-w-0">
                            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground leading-tight">{t('vacation.balance.total_label')}</p>
                            <p className="text-xl sm:text-3xl font-bold font-mono mt-1 truncate text-primary">{balance?.total_available || 0}</p>
                            <p className="text-[10px] text-muted-foreground">days</p>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-amber-200 bg-amber-50/50 min-w-0">
                    <CardContent className="pt-6">
                        <div className="text-center min-w-0">
                            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-amber-700 leading-tight">{t('vacation.balance.planned_label')}</p>
                            <p className="text-xl sm:text-3xl font-bold font-mono mt-1 truncate text-amber-600">{pendingDays}</p>
                            <p className="text-[10px] text-amber-600">days</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Gantt Calendar View */}
            {viewMode === 'gantt' && (
                <>
                    <div className="sm:hidden">
                        <Card className="border border-border shadow-none">
                            <CardContent className="p-8 text-center text-muted-foreground">
                                <LayoutGrid className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                <p>Calendar view is available on desktop</p>
                            </CardContent>
                        </Card>
                    </div>
                    <div className="hidden sm:block">
                <Card>
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            {/* Period Navigation */}
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => navigatePeriod(-1)}>
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <span className="font-medium min-w-[200px] text-center">{getPeriodLabel()}</span>
                                <Button variant="outline" size="sm" onClick={() => navigatePeriod(1)}>
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setCurrentYear(new Date().getFullYear());
                                        setCurrentMonth(new Date().getMonth());
                                    }}
                                >
                                    {t('vacation.calendar.today_button')}
                                </Button>
                            </div>
                            
                            {/* View Options */}
                            <div className="flex items-center gap-2">
                                <Label className="text-xs uppercase text-muted-foreground">{t('vacation.calendar.view_label')}</Label>
                                <select
                                    value={viewRange}
                                    onChange={(e) => setViewRange(e.target.value)}
                                    className="h-8 px-2 border border-input bg-background text-sm rounded-md"
                                >
                                    <option value="month">{t('vacation.calendar.view_month')}</option>
                                    <option value="quarter">{t('vacation.calendar.view_quarter')}</option>
                                </select>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {/* Legend */}
                        <div className="flex items-center gap-6 px-4 py-2 border-b bg-muted/30 text-xs">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-3 bg-green-500 rounded"></div>
                                <span>{t('vacation.calendar.legend.approved')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-3 bg-amber-500 rounded"></div>
                                <span>{t('vacation.calendar.legend.pending')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-3 bg-red-200 rounded"></div>
                                <span>{t('vacation.calendar.legend.red_day')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-3 bg-muted rounded"></div>
                                <span>{t('vacation.calendar.legend.weekend')}</span>
                            </div>
                        </div>

                        {/* Gantt Chart */}
                        <div className="overflow-x-auto">
                            <div className="min-w-[600px]">
                                {/* Week Headers */}
                                <div className="flex border-b">
                                    <div className="w-32 flex-shrink-0 p-2 bg-muted/50 border-r font-medium text-sm">
                                        {t('vacation.calendar.header')}
                                    </div>
                                    <div className="flex-1 flex">
                                        {weekGroups.map((group, idx) => (
                                            <div 
                                                key={idx}
                                                className="border-r bg-muted/30 text-center text-xs font-medium py-1"
                                                style={{ width: `${(group.days.length / timelineDays.length) * 100}%` }}
                                            >
                                                {t('vacation.calendar.week_header', { num: group.week })}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Day Headers */}
                                <div className="flex border-b">
                                    <div className="w-32 flex-shrink-0 p-2 bg-muted/50 border-r text-xs text-muted-foreground">
                                        {t('vacation.calendar.plans_count', { count: plansInView.length })}
                                    </div>
                                    <div className="flex-1 flex">
                                        {timelineDays.map((day, idx) => (
                                            <div 
                                                key={idx}
                                                className={`flex-1 text-center text-[10px] py-1 border-r last:border-r-0 ${
                                                    day.isRedDay ? 'bg-red-100 text-red-700' :
                                                    day.isWeekend ? 'bg-muted/50 text-muted-foreground' : ''
                                                }`}
                                                title={day.date}
                                            >
                                                <div className="font-medium">{day.day}</div>
                                                <div className="text-[9px]">{shortDayNames[day.dayOfWeek === 0 ? 6 : day.dayOfWeek - 1]}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Vacation Rows */}
                                {plansInView.length === 0 ? (
                                    <div className="flex items-center justify-center py-8 text-muted-foreground">
                                        <div className="text-center">
                                            <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">{t('vacation.calendar.empty_state')}</p>
                                        </div>
                                    </div>
                                ) : (
                                    plansInView.map(plan => (
                                        <div key={plan.id} className="flex border-b hover:bg-muted/20">
                                            {/* Plan Info */}
                                            <div className="w-32 flex-shrink-0 p-2 border-r flex items-center gap-2">
                                                <div className="min-w-0">
                                                    <p className="text-xs font-medium truncate">
                                                        {formatDate(plan.start_date)}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">{plan.days_count}d • {plan.status}</p>
                                                </div>
                                            </div>
                                            
                                            {/* Timeline */}
                                            <div className="flex-1 relative h-12">
                                                {/* Day backgrounds */}
                                                <div className="absolute inset-0 flex">
                                                    {timelineDays.map((day, idx) => (
                                                        <div 
                                                            key={idx}
                                                            className={`flex-1 border-r last:border-r-0 ${
                                                                day.isRedDay ? 'bg-red-50' :
                                                                day.isWeekend ? 'bg-muted/30' : ''
                                                            }`}
                                                        />
                                                    ))}
                                                </div>
                                                
                                                {/* Vacation bar */}
                                                {(() => {
                                                    const style = getVacationBarStyle(plan);
                                                    if (!style) return null;
                                                    
                                                    return (
                                                        <div
                                                            className={`absolute h-6 rounded cursor-pointer transition-all hover:opacity-80 hover:scale-[1.02] flex items-center justify-center text-white text-[10px] font-medium shadow-sm ${
                                                                plan.status === 'approved' ? 'bg-green-500' : 'bg-amber-500'
                                                            }`}
                                                            style={{ ...style, top: '50%', transform: 'translateY(-50%)' }}
                                                            title={`Click to view/edit: ${plan.start_date} - ${plan.end_date} (${plan.days_count}d)`}
                                                            onClick={() => {
                                                                setSelectedPlan(plan);
                                                                setViewDialogOpen(true);
                                                            }}
                                                            data-testid={`vacation-bar-${plan.id}`}
                                                        >
                                                            {plan.days_count}d
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
                    </div>
                </>
            )}

            {/* List View */}
            {viewMode === 'list' && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-lg">{t('vacation.list.title')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {plans.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>{t('vacation.list.empty_state')}</p>
                                <p className="text-sm">{t('vacation.list.empty_hint')}</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {plans.map(plan => (
                                    <div 
                                        key={plan.id} 
                                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                                        data-testid={`vacation-plan-${plan.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                                                <Calendar className="w-6 h-6 text-primary" />
                                            </div>
                                            <div>
                                                <p className="font-medium">
                                                    {plan.start_date !== plan.end_date
                                                        ? `${formatDate(plan.start_date)} — ${formatDate(plan.end_date)}`
                                                        : formatDate(plan.start_date)}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    {t('vacation.list.days_label', { count: plan.days_count })}
                                                    {plan.notes && ` • ${plan.notes}`}
                                                </p>
                                                {(plan.status === 'approved' || plan.status === 'rejected') && plan.reviewer_name && (
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {plan.status === 'approved' ? t('vacation.list.approved_by', { name: plan.reviewer_name }) : t('vacation.list.rejected_by', { name: plan.reviewer_name })}
                                                        {plan.review_comment && `: "${plan.review_comment}"`}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-3">
                                            <span className={`px-2 py-1 rounded text-xs text-white flex items-center gap-1 ${getStatusColor(plan.status)}`}>
                                                {getStatusIcon(plan.status)}
                                                {plan.status}
                                            </span>
                                            
                                            {(plan.status === 'pending' || plan.status === 'approved') && (
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    onClick={() => handleCancelPlan(plan.id)}
                                                    className="text-destructive hover:text-destructive"
                                                    data-testid={`cancel-plan-${plan.id}`}
                                                    title="Cancel vacation"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* New Plan Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-primary" />
                            {t('vacation.dialog.title')}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Available balance info */}
                        <div className="p-3 bg-muted rounded-lg text-sm">
                            <div className="flex justify-between">
                                <span>{t('vacation.dialog.available_label')}</span>
                                <span className="font-bold">{balance?.total_available || 0} {t('common.days_unit')}</span>
                            </div>
                            {pendingDays > 0 && (
                                <div className="flex justify-between text-amber-600 mt-1">
                                    <span>{t('vacation.dialog.planned_label')}</span>
                                    <span className="font-bold">-{pendingDays} days</span>
                                </div>
                            )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="label-uppercase mb-2 block">{t('vacation.dialog.start_label')}</Label>
                                <Input
                                    type="date"
                                    value={newPlan.start_date}
                                    onChange={(e) => setNewPlan({ ...newPlan, start_date: e.target.value })}
                                    min={new Date().toISOString().split('T')[0]}
                                    data-testid="start-date-input"
                                />
                            </div>
                            <div>
                                <Label className="label-uppercase mb-2 block">{t('vacation.dialog.end_label')}</Label>
                                <Input
                                    type="date"
                                    value={newPlan.end_date}
                                    onChange={(e) => setNewPlan({ ...newPlan, end_date: e.target.value })}
                                    min={newPlan.start_date || new Date().toISOString().split('T')[0]}
                                    data-testid="end-date-input"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('vacation.dialog.notes_label')}</Label>
                            <Textarea
                                value={newPlan.notes}
                                onChange={(e) => setNewPlan({ ...newPlan, notes: e.target.value })}
                                placeholder={t('vacation.dialog.notes_placeholder')}
                                rows={2}
                                data-testid="notes-input"
                            />
                        </div>
                        
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                            <AlertCircle className="w-4 h-4 inline mr-2" />
                            {t('vacation.dialog.info_alert')}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleCreatePlan}
                            disabled={saving || !newPlan.start_date || !newPlan.end_date}
                            data-testid="submit-vacation-plan-btn"
                        >
                            {saving ? t('common.saving') : t('vacation.dialog.submit_button')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View/Edit Plan Dialog */}
            <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-primary" />
                            {t('vacation.details.dialog_title')}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedPlan && (
                        <div className="space-y-4 py-4">
                            {/* Status Badge */}
                            <div className="flex justify-center">
                                <span className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 ${
                                    selectedPlan.status === 'approved' ? 'bg-green-100 text-green-700' :
                                    selectedPlan.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                    selectedPlan.status === 'cancelled' ? 'bg-gray-100 text-gray-700' :
                                    'bg-amber-100 text-amber-700'
                                }`}>
                                    {getStatusIcon(selectedPlan.status)}
                                    {selectedPlan.status.charAt(0).toUpperCase() + selectedPlan.status.slice(1)}
                                </span>
                            </div>
                            
                            {/* Dates */}
                            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                                <div className="text-center">
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('vacation.details.start_label')}</p>
                                    <p className="font-bold text-lg">
                                        {formatDate(selectedPlan.start_date)}
                                    </p>
                                </div>
                                <div className="text-center">
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('vacation.details.end_label')}</p>
                                    <p className="font-bold text-lg">
                                        {formatDate(selectedPlan.end_date)}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Duration */}
                            <div className="text-center">
                                <p className="text-3xl font-bold text-primary">{selectedPlan.days_count}</p>
                                <p className="text-sm text-muted-foreground">{t('vacation.details.days_label')}</p>
                            </div>
                            
                            {/* Notes */}
                            {selectedPlan.notes && (
                                <div className="p-3 bg-muted/30 rounded-lg">
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('vacation.details.notes_label')}</p>
                                    <p className="text-sm">{selectedPlan.notes}</p>
                                </div>
                            )}
                            
                            {/* Reviewer Info */}
                            {(selectedPlan.status === 'approved' || selectedPlan.status === 'rejected') && selectedPlan.reviewer_name && (
                                <div className="p-3 border rounded-lg">
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                                        {t('vacation.details.reviewer_label')}
                                    </p>
                                    <p className="font-medium">{selectedPlan.reviewer_name}</p>
                                    {selectedPlan.review_comment && (
                                        <p className="text-sm text-muted-foreground mt-1 italic">
                                            &ldquo;{selectedPlan.review_comment}&rdquo;
                                        </p>
                                    )}
                                </div>
                            )}
                            
                            {/* Warning for cancellation */}
                            {(selectedPlan.status === 'pending' || selectedPlan.status === 'approved') && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                                    <AlertCircle className="w-4 h-4 inline mr-2" />
                                    {selectedPlan.status === 'pending'
                                        ? t('vacation.details.pending_warning')
                                        : t('vacation.details.approved_warning')}
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter className="flex gap-2">
                        <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                            {t('common.close')}
                        </Button>
                        {selectedPlan && (selectedPlan.status === 'pending' || selectedPlan.status === 'approved') && (
                            <Button
                                variant="destructive"
                                onClick={() => {
                                    handleCancelPlan(selectedPlan.id);
                                    setViewDialogOpen(false);
                                }}
                                data-testid="cancel-plan-dialog-btn"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {t('vacation.details.cancel_button')}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
